/**
 * What stops this workshop from getting paid (#2469).
 *
 * These rules are not a new definition of "complete" — they mirror the two
 * places on the server that already refuse to issue a document, so a workshop
 * learns about a gap on the settings page instead of when a customer is
 * standing at the counter:
 *
 *   - blocksPayment: services/staff-api/src/lib/receipt-seller.ts:43-48
 *   - blocksInvoice: services/staff-api/src/lib/finalize-draft-invoice.ts:62
 *
 * If a server-side requirement changes, this file changes in the same commit;
 * workshopReadiness.test.ts names the source line behind every rule so the
 * pairing is checkable rather than remembered.
 *
 * The one rule with no server counterpart is the labor rate. Nothing rejects a
 * zero rate — the work is simply invoiced at nothing, silently — so it is a
 * warning rather than a blocker. That is exactly why it is worth surfacing:
 * no error will ever mention it.
 */

import type { WorkshopSettings } from '@/types';

/**
 * How badly a gap hurts. Kept apart because the consequences genuinely differ:
 * a receipt cannot be issued at all, an invoice cannot be finalized (but a
 * cash sale still works — checkout-service.ts passes requireBankDetails:false),
 * and a zero rate fails silently.
 */
export type ReadinessSeverity =
  | 'blocksPayment'
  | 'blocksInvoice'
  /**
   * Stops work being done at all rather than stopping money moving (#2488).
   * These have no server counterpart: nothing throws, the workshop simply
   * cannot create a work order, fill a calendar or assign anyone.
   */
  | 'blocksWork'
  | 'warning';

/** An anchor inside the workshop tab's own form. */
export type ReadinessSection = 'identity' | 'billing' | 'pricing';

/**
 * Where the fix lives. Until #2488 every gap was on the workshop tab, so a
 * section name was enough; services, lifts and staff live on other tabs, so
 * the target now carries the tab too.
 */
export interface ReadinessTarget {
  /**
   * Identity stayed on `workshop` so existing deep links keep working.
   * Billing and the labour rate moved to the pricing page (#2428).
   */
  tab: 'workshop' | 'workshop-pricing' | 'workshop-hours' | 'services' | 'equipment' | 'roles';
  /** Only meaningful on a workshop page that still hosts that section. */
  section?: ReadinessSection;
}

export interface ReadinessIssue {
  /** Translation key under `readiness.issue` in the settings namespace. */
  key: string;
  severity: ReadinessSeverity;
  section: ReadinessSection;
  target: ReadinessTarget;
}

/**
 * What the settings object cannot tell us. Each is a row count from its own
 * query, passed in so the rule function stays pure and testable without
 * mocking a query client — see useWorkshopReadiness for the wiring.
 *
 * Undefined means "not loaded yet" and is deliberately distinct from zero: a
 * page that is still fetching must not accuse the workshop of having no
 * mechanics.
 */
export interface ReadinessCounts {
  services?: number | undefined;
  lifts?: number | undefined;
  mechanics?: number | undefined;
}

function isBlank(value: string | null | undefined): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

export function workshopReadinessIssues(
  settings: WorkshopSettings,
  counts: ReadinessCounts = {},
): ReadinessIssue[] {
  const issues: ReadinessIssue[] = [];

  // receipt-seller.ts:44
  if (isBlank(settings.businessId)) {
    issues.push({ key: 'businessId', severity: 'blocksPayment', section: 'identity', target: { tab: 'workshop', section: 'identity' } });
  }

  // receipt-seller.ts:45 — one token for the three address parts, matching the
  // server, which reports a single 'address' rather than naming which part.
  if (isBlank(settings.address) || isBlank(settings.city) || isBlank(settings.postalCode)) {
    issues.push({ key: 'address', severity: 'blocksPayment', section: 'identity', target: { tab: 'workshop', section: 'identity' } });
  }

  // receipt-seller.ts:46 — either one satisfies the server, so this is only
  // missing when both are.
  if (isBlank(settings.email) && isBlank(settings.phone)) {
    issues.push({ key: 'contact', severity: 'blocksPayment', section: 'identity', target: { tab: 'workshop', section: 'identity' } });
  }

  // finalize-draft-invoice.ts:62
  if (isBlank(settings.bankIban)) {
    issues.push({ key: 'bankIban', severity: 'blocksInvoice', section: 'billing', target: { tab: 'workshop-pricing', section: 'billing' } });
  }

  // No server rule: a zero rate bills the work at nothing without complaint.
  const rate = settings.laborRateGrossCents ?? settings.laborRateCents ?? 0;
  if (rate <= 0) {
    issues.push({ key: 'laborRate', severity: 'warning', section: 'pricing', target: { tab: 'workshop-pricing', section: 'pricing' } });
  }

  // Nothing below has a server rule to mirror — no endpoint throws. They are
  // the difference between a workshop that can be paid and one that can
  // actually work, and each names what it blocks.
  //
  // `undefined` is skipped on purpose: a still-loading count must not read as
  // an empty one.
  if (counts.services === 0) {
    issues.push({
      key: 'services',
      severity: 'blocksWork',
      section: 'pricing',
      target: { tab: 'services' },
    });
  }

  if (counts.lifts === 0) {
    issues.push({
      key: 'lifts',
      severity: 'blocksWork',
      section: 'pricing',
      target: { tab: 'equipment' },
    });
  }

  if (counts.mechanics === 0) {
    issues.push({
      key: 'mechanics',
      severity: 'blocksWork',
      section: 'identity',
      target: { tab: 'roles' },
    });
  }

  return issues;
}

/** True when something stops a document being issued or work being done. */
export function hasBlockingIssues(issues: ReadinessIssue[]): boolean {
  return issues.some((issue) => issue.severity !== 'warning');
}
